// Running Balance Panel — shared across Bill Booking (supplier) and
// Customer Payments (customer). Shows 4 KPI cards for the selected party
// within a Financial Year (Apr–Mar).
//
// Props:
//   • kind:            "supplier" | "customer"  — chooses backend endpoint
//   • party:           { id, name }             — the selected party (null hides the panel)
//   • defaultFy:       optional "YYYY-YY"       — overrides current-FY default
//
// Formula:  opening + total_purchase(or sales) − total_paid = closing
// Opening is auto (rolled from previous FY's closing) but manually editable
// inline. Clearing the manual value falls back to auto.

import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Wallet, TrendingUp, Banknote, Scale, Pencil, Save, X, RotateCcw } from "lucide-react";

const fmtINR = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const currentFY = () => {
  const now = new Date();
  const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${y}-${String(y + 1).slice(-2)}`;
};

const fyOptions = (n = 6) => {
  const cur = currentFY();
  const start = parseInt(cur.split("-")[0], 10);
  const opts = [];
  for (let i = 2; i >= -3; i--) {
    const y = start + i;
    opts.push(`${y}-${String(y + 1).slice(-2)}`);
  }
  return opts;
};

export default function RunningBalancePanel({ kind, party, defaultFy }) {
  const [fy, setFy] = useState(defaultFy || currentFY());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [openingDraft, setOpeningDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const endpoint = kind === "customer" ? "customer-running-balance" : "supplier-running-balance";
  const partyLabel = kind === "customer" ? "Customer" : "Supplier";
  const flowLabel = kind === "customer" ? "Total Sales" : "Total Purchase";

  const load = async () => {
    if (!party?.id) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/${endpoint}/${party.id}`, { params: { fy } });
      setData(d);
      setOpeningDraft(String(d.opening_balance ?? ""));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load running balance");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [party?.id, fy]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveOpening = async () => {
    setSaving(true);
    try {
      const val = openingDraft.trim() === "" ? null : Number(openingDraft);
      if (openingDraft.trim() !== "" && Number.isNaN(val)) {
        toast.error("Enter a number");
        setSaving(false);
        return;
      }
      const { data: d } = await api.put(`/${endpoint}/${party.id}`, { fy, opening_balance: val });
      setData(d);
      setOpeningDraft(String(d.opening_balance ?? ""));
      toast.success(val === null ? "Manual override cleared" : "Opening balance saved");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sourceBadge = useMemo(() => {
    if (!data) return null;
    const s = data.opening_source;
    if (s === "manual") return <span className="text-[9px] bg-amber-100 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5 font-semibold uppercase tracking-wider">Manual</span>;
    if (s === "auto") return <span className="text-[9px] bg-sky-100 text-sky-800 border border-sky-200 rounded px-1.5 py-0.5 font-semibold uppercase tracking-wider">Auto</span>;
    return <span className="text-[9px] bg-slate-100 text-slate-500 border border-slate-200 rounded px-1.5 py-0.5 font-semibold uppercase tracking-wider">—</span>;
  }, [data]);

  if (!party?.id) return null;

  return (
    <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-lg p-4" data-testid={`running-balance-${kind}`}>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{partyLabel} Running Balance</div>
          <div className="text-sm font-semibold text-slate-900 mt-0.5">{party.name}</div>
        </div>
        <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2 py-1">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">FY</span>
          <select
            value={fy}
            onChange={(e) => setFy(e.target.value)}
            data-testid={`running-balance-${kind}-fy`}
            className="bg-transparent text-xs font-mono text-slate-900 focus:outline-none"
          >
            {fyOptions().map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* Opening */}
        <div className="bg-white border border-slate-200 rounded-md p-3">
          <div className="flex items-center justify-between mb-1">
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              <Wallet size={11} className="text-slate-400" /> Opening
            </div>
            {sourceBadge}
          </div>
          {editing ? (
            <div className="flex items-center gap-1">
              <input
                type="number"
                step="0.01"
                autoFocus
                value={openingDraft}
                onChange={(e) => setOpeningDraft(e.target.value)}
                placeholder="0.00"
                data-testid={`running-balance-${kind}-opening-input`}
                className="w-full border border-slate-300 rounded px-2 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1D2951]/30"
              />
              <button onClick={saveOpening} disabled={saving} data-testid={`running-balance-${kind}-opening-save`}
                className="p-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded" title="Save">
                <Save size={12} />
              </button>
              <button onClick={() => { setEditing(false); setOpeningDraft(String(data?.opening_balance ?? "")); }}
                className="p-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded" title="Cancel">
                <X size={12} />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-1">
              <div className="font-mono font-semibold text-slate-900" data-testid={`running-balance-${kind}-opening`}>
                {loading ? "…" : fmtINR(data?.opening_balance)}
              </div>
              <div className="inline-flex items-center gap-1">
                <button onClick={() => setEditing(true)} data-testid={`running-balance-${kind}-opening-edit`}
                  className="p-1 text-slate-500 hover:text-[#1D2951]" title="Edit manually">
                  <Pencil size={12} />
                </button>
                {data?.opening_source === "manual" && (
                  <button onClick={() => { setOpeningDraft(""); saveOpening(); }} data-testid={`running-balance-${kind}-opening-reset`}
                    className="p-1 text-slate-500 hover:text-sky-700" title="Clear manual override (auto-roll from previous FY)">
                    <RotateCcw size={12} />
                  </button>
                )}
              </div>
            </div>
          )}
          {data?.opening_source === "auto" && !editing && (
            <div className="text-[9px] text-slate-400 mt-0.5 italic">rolled from previous FY closing</div>
          )}
        </div>

        {/* Purchase / Sales */}
        <div className="bg-white border border-slate-200 rounded-md p-3">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
            <TrendingUp size={11} className="text-slate-400" /> {flowLabel}
          </div>
          <div className="font-mono font-semibold text-[#1D2951]" data-testid={`running-balance-${kind}-purchase`}>
            {loading ? "…" : fmtINR(data?.total_purchase)}
          </div>
        </div>

        {/* Paid */}
        <div className="bg-white border border-slate-200 rounded-md p-3">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
            <Banknote size={11} className="text-slate-400" /> Total Paid
          </div>
          <div className="font-mono font-semibold text-emerald-700" data-testid={`running-balance-${kind}-paid`}>
            {loading ? "…" : fmtINR(data?.total_paid)}
          </div>
        </div>

        {/* Closing */}
        <div className={`rounded-md p-3 border ${Number(data?.closing_balance || 0) > 0 ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"}`}>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
            <Scale size={11} className="text-slate-400" /> Closing
          </div>
          <div className={`font-mono font-bold text-lg ${Number(data?.closing_balance || 0) > 0 ? "text-rose-800" : "text-emerald-800"}`}
               data-testid={`running-balance-${kind}-closing`}>
            {loading ? "…" : fmtINR(data?.closing_balance)}
          </div>
          <div className="text-[9px] text-slate-500 mt-0.5 font-mono">
            Opening + {kind === "customer" ? "Sales" : "Purchase"} − Paid
          </div>
        </div>
      </div>
    </div>
  );
}
